/**
 * Session state.
 *
 * The token is the session. There is no refresh token yet (CORE-3 on the build
 * board), so a session lasts as long as the access token and then ends
 * cleanly — the user is told why, and lands back where they were after signing
 * in again, rather than being dumped on a blank dashboard.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ApiError, api, readToken, setAuthFailureHandler, transport, writeToken } from "./api";
import { device, isRegisteredElsewhere, newDevice } from "./device";
import * as outbox from "./outbox";
import { QUEUE_OWNER, readMeta, wipe, wipeKeepingQueue, writeMeta } from "./store";
import { syncNow } from "./sync";
import type { Role, TokenResponse } from "./types";

export interface Session {
  userId: string;
  orgId: string;
  role: Role;
  email: string;
  /** Somebody else chose their password; nothing else until they choose one. */
  mustChangePassword?: boolean;
  fullName?: string | null;
}

type Status = "checking" | "authenticated" | "anonymous";

interface AuthValue {
  status: Status;
  session: Session | null;
  /** Set when a session ended on its own, so the login page can explain why. */
  expired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Take a token handed back by first-run setup or a password change. */
  adopt: (token: TokenResponse, email: string) => void;
  signOut: () => Promise<void>;
  can: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

type QueueOwner = { id: string; name: string };

/**
 * Decide what happens to unsent work already on this phone when somebody signs in.
 *
 * Their own: it is theirs, and it sends. Somebody else's: it can only be sent
 * by them — the server ties a device's changes to the user it is registered to
 * — so the person signing in is told whose it is and chooses, rather than it
 * vanishing or going up under the wrong name (D84).
 */
async function claimQueue(userId: string, name: string): Promise<void> {
  const owner = await readMeta<QueueOwner | null>(QUEUE_OWNER, null).catch(() => null);
  const who = owner?.name.replace(/\.+$/, "") ?? "";
  const { pending } = await outbox.counts().catch(() => ({ pending: 0, rejected: 0 }));
  if (pending > 0 && owner !== null && owner.id !== userId) {
    const discard = window.confirm(
      `This phone has ${pending} unsent change${pending === 1 ? "" : "s"} from ${who}. ` +
        `Only ${who} can send ${pending === 1 ? "it" : "them"}. ` +
        "Discard them and sign in? (Cancel, and ask them to sign in here first.)",
    );
    if (!discard) {
      throw new ApiError(409, `Ask ${who} to sign in on this phone and send their changes first.`);
    }
    await wipe();
  }
  await writeMeta<QueueOwner>(QUEUE_OWNER, { id: userId, name });
}

/**
 * Moving this phone to another business (D93). Anything still unsent belongs
 * to the old one and can only be sent by signing in there, so the person is
 * asked before it goes; then the phone is wiped and takes a new identity.
 */
async function leaveOtherBusiness(): Promise<void> {
  const owner = await readMeta<QueueOwner | null>(QUEUE_OWNER, null).catch(() => null);
  const who = owner?.name.replace(/\.+$/, "") ?? "";
  const { pending } = await outbox.counts().catch(() => ({ pending: 0, rejected: 0 }));
  if (pending > 0) {
    const discard = window.confirm(
      `This phone has ${pending} unsent change${pending === 1 ? "" : "s"} for another business` +
        `${who ? ` (from ${who})` : ""}. They can only be sent by signing in to that business. ` +
        "Discard them and sign in here? (Cancel, and sign in there first to send them.)",
    );
    if (!discard) {
      throw new ApiError(409, `Sign in as ${who || "whoever made them"} first, to send the unsent changes.`);
    }
  }
  await wipe();
  newDevice();
}

const EMAIL_KEY = "keystone.email";

function sessionFrom(token: TokenResponse, email: string): Session {
  return {
    userId: token.user_id,
    orgId: token.org_id,
    role: token.role,
    email,
    mustChangePassword: Boolean(token.must_change_password),
    fullName: token.full_name ?? null,
  };
}


/**
 * Read a token's own claims, without the server.
 *
 * Used only when the server cannot be reached, and only to keep a tech signed
 * in to their own device. The signature is not checked here — it cannot be —
 * so this decides *nothing* about access: it decides what to draw while
 * offline. Every request still carries the token and is verified properly.
 *
 * Returns null for anything malformed or past its expiry, so a stale token
 * ends the session the way it should.
 */
function readClaims(token: string | null): { sub: string; org: string; role: Role } | null {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { sub?: string; org?: string; role?: string; exp?: number };

    if (!claims.sub || !claims.org) return null;
    if (typeof claims.exp === "number" && claims.exp * 1000 <= Date.now()) return null;

    return { sub: claims.sub, org: claims.org, role: (claims.role ?? "technician") as Role };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");
  const [session, setSession] = useState<Session | null>(null);
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;
  const [expired, setExpired] = useState(false);

  const endSession = useCallback((wasExpiry: boolean) => {
    writeToken(null);
    setSession(null);
    setStatus("anonymous");
    setExpired(wasExpiry);
  }, []);

  // A 401 from any call ends the session once, wherever it happened.
  useEffect(() => {
    setAuthFailureHandler(() => endSession(true));
    return () => setAuthFailureHandler(null);
  }, [endSession]);

  // On load, a stored token is a claim, not proof. Verify it before showing
  // the app — otherwise the first screen renders and then collapses into a
  // login page, which reads as a bug.
  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!readToken()) {
        if (!cancelled) setStatus("anonymous");
        return;
      }
      try {
        const me = await api.auth.me();
        if (cancelled) return;
        let email = "";
        try {
          email = localStorage.getItem(EMAIL_KEY) ?? "";
        } catch {
          /* ignore */
        }
        setSession({
          userId: me.user_id,
          orgId: me.org_id,
          role: me.role,
          email: me.email ?? email,
          mustChangePassword: Boolean(me.must_change_password),
          fullName: me.full_name ?? null,
        });
        setStatus("authenticated");
      } catch (error) {
        if (cancelled) return;

        // Unreachable is not unauthorised. A tech who opens the app in a comms
        // room must not be signed out of their own device — they would lose
        // access to the working set and the queue, which is the exact moment
        // the app is supposed to be most useful. The stored token is trusted
        // locally instead, and only until its own expiry: it is a signed JWT,
        // so the device can read `exp` without asking anyone. Nothing is
        // granted by this — every real call is still verified server-side.
        if (error instanceof ApiError && error.status === 0) {
          const claims = readClaims(readToken());
          if (claims) {
            let email = "";
            try {
              email = localStorage.getItem(EMAIL_KEY) ?? "";
            } catch {
              /* ignore */
            }
            setSession({ userId: claims.sub, orgId: claims.org, role: claims.role, email });
            setStatus("authenticated");
            return;
          }
        }

        // An expired token is an ordinary end of session, not an error worth
        // an alarming message on first load.
        endSession(false);
      }
    }

    void verify();
    return () => {
      cancelled = true;
    };
  }, [endSession]);

  // The role on screen follows the server's, not the one signed in with
  // (round 13): a person moved from office to supervisor in another browser
  // kept the office's editors until they reloaded, though every save was
  // refused. Re-read who this is when the app comes back into view, and every
  // few minutes while it is open. Only an answer changes anything — offline,
  // or a failed check, leaves the session as it is.
  useEffect(() => {
    if (status !== "authenticated") return;
    let busy = false;
    async function recheck() {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try {
        const me = await api.auth.me();
        // A different person's token under this tab (signed in elsewhere in
        // the same browser): start again from it, so the queue and the
        // working set are theirs and not the last person's.
        if (sessionRef.current && sessionRef.current.userId !== me.user_id) {
          window.location.reload();
          return;
        }
        setSession((current) =>
          current && (current.role !== me.role || current.fullName !== (me.full_name ?? null))
            ? { ...current, role: me.role, fullName: me.full_name ?? null }
            : current,
        );
      } catch (error) {
        // A role change, a reset or being switched off ends the session on the
        // server (D53); say so now rather than at the next save. An outage is
        // not a demotion, so anything else leaves the session alone.
        if (error instanceof ApiError && error.status === 401) endSession(true);
      } finally {
        busy = false;
      }
    }
    const onVisible = () => void recheck();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const timer = window.setInterval(onVisible, 5 * 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.clearInterval(timer);
    };
  }, [status, endSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    let token: TokenResponse;
    try {
      token = await api.auth.login(email.trim().toLowerCase(), password, device().id);
    } catch (error) {
      if (!isRegisteredElsewhere(error)) throw error;
      // This phone last worked for another business (D93). Its rows, its queue
      // and its device id belong there; this business gets a clean phone.
      await leaveOtherBusiness();
      token = await api.auth.login(email.trim().toLowerCase(), password, device().id);
    }
    await claimQueue(token.user_id, token.full_name ?? email.trim().toLowerCase());
    writeToken(token.access_token);
    try {
      localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
    } catch {
      /* ignore */
    }
    setSession(sessionFrom(token, email.trim().toLowerCase()));
    setExpired(false);
    setStatus("authenticated");
  }, []);

  const adopt = useCallback((token: TokenResponse, email: string) => {
    void writeMeta(QUEUE_OWNER, { id: token.user_id, name: token.full_name ?? email }).catch(() => {});
    writeToken(token.access_token);
    try {
      localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
    } catch {
      /* ignore */
    }
    setSession(sessionFrom(token, email.trim().toLowerCase()));
    setExpired(false);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(async () => {
    // Unsent work first. Try to send it; if it still cannot go, say so before
    // anything is cleared, and keep it for the person it belongs to (D84).
    let { pending } = await outbox.counts().catch(() => ({ pending: 0, rejected: 0 }));
    if (pending > 0) {
      await syncNow(transport).catch(() => undefined);
      pending = (await outbox.counts().catch(() => ({ pending: 0, rejected: 0 }))).pending;
    }
    if (
      pending > 0 &&
      !window.confirm(
        `${pending} change${pending === 1 ? " hasn't" : "s haven't"} been sent yet — there's no signal. ` +
          "Sign out anyway? They stay on this phone and send next time you sign in here.",
      )
    ) {
      return;
    }
    try {
      localStorage.removeItem(EMAIL_KEY);
    } catch {
      /* ignore */
    }
    // Forget the working set as well as the token. A shared van iPad handed to
    // the next tech must not still be carrying the last one's jobs, their
    // customers' access notes and their van's stock — but it keeps their
    // unsent work, which nobody else can see or send. Fire-and-forget: a store
    // that will not clear must not trap someone in a session.
    void (pending > 0 ? wipeKeepingQueue() : wipe()).catch(() => {});
    endSession(false);
  }, [endSession]);

  const can = useCallback(
    (...roles: Role[]) => (session ? roles.includes(session.role) : false),
    [session],
  );

  const value = useMemo<AuthValue>(
    () => ({ status, session, expired, signIn, adopt, signOut, can }),
    [status, session, expired, signIn, adopt, signOut, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}

/** Office-and-up. Techs see their own work; they do not reprice quotes. */
export const OFFICE: Role[] = ["owner", "office", "supervisor"];
